import { useEffect, useState } from 'react';

export const TOUR_HEADER_OFFSET = 64;

export type SpotRect = { top: number; left: number; width: number; height: number };

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isUsable = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (element.closest('[aria-hidden="true"]')) return false;
  return window.getComputedStyle(element).visibility !== 'hidden';
};

export const findTarget = (id: string): HTMLElement | null => {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour-id="${id}"]`));
  if (process.env.NODE_ENV !== 'production' && nodes.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`[tour] ${nodes.length} elements share data-tour-id="${id}"; ids must be unique while mounted.`);
  }
  const ready = nodes.find(node => node.dataset.tourReady === 'true' && isUsable(node));
  return ready || nodes.find(isUsable) || null;
};

export const waitForTarget = (id: string, timeout = 4000): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    const immediate = findTarget(id);
    if (immediate) { resolve(immediate); return; }
    let settled = false;
    const finish = (element: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(element);
    };
    const observer = new MutationObserver(() => {
      const element = findTarget(id);
      if (element) finish(element);
    });
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-tour-id', 'data-tour-ready', 'style', 'class', 'aria-hidden'],
    });
    const timer = window.setTimeout(() => finish(null), timeout);
  });

// Scrolls the target clear of the fixed 64px header, then waits for the scroll to settle
// so the spotlight is never painted against a stale rect.
export const scrollTargetIntoView = (element: HTMLElement): Promise<void> =>
  new Promise(resolve => {
    const rect = element.getBoundingClientRect();
    const safeTop = TOUR_HEADER_OFFSET + 24;
    const safeBottom = window.innerHeight - 24;
    if (rect.top >= safeTop && (rect.bottom <= safeBottom || rect.height > window.innerHeight - safeTop - 24)) {
      resolve();
      return;
    }
    window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - safeTop), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    let last = Number.NaN;
    let stable = 0;
    let frames = 0;
    const tick = () => {
      const position = window.scrollY;
      stable = Math.abs(position - last) < 0.5 ? stable + 1 : 0;
      last = position;
      frames += 1;
      if (stable >= 2 || frames > 40) resolve();
      else window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

export const useTrackedRect = (element: HTMLElement | null, active: boolean): SpotRect | null => {
  const [rect, setRect] = useState<SpotRect | null>(null);
  useEffect(() => {
    if (!element || !active) { setRect(null); return; }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const next = element.getBoundingClientRect();
      setRect(previous => (
        previous && previous.top === next.top && previous.left === next.left
          && previous.width === next.width && previous.height === next.height
          ? previous
          : { top: next.top, left: next.left, width: next.width, height: next.height }
      ));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [element, active]);
  return rect;
};
